import {
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  effect,
  ElementRef,
  inject,
  viewChild,
  ViewEncapsulation,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { NewHireDialogService } from '@app/+authenticated/+employees/new-hires/services/new-hire-dialog.service';
import { TemplateDialogService } from '@app/+authenticated/+employees/templates/contract-template/services/template-dialog.service';
import { DefaultLocale } from '@app/+authenticated/shared/locale/locale.helper';
import { getAccountSubscription } from '@app/reducers/account/account-service.selectors';
import { getPermissionState } from '@app/reducers/auth/permission.helper';
import { AppState } from '@app/reducers/interfaces';
import { getActiveEmployees, getAuthenticatedUser } from '@app/reducers/orm/employee/employee.selectors';
import { userPreferencesStore } from '@app/signal-store/user-preferences/user-preferences.store';
import { Events } from '@ngrx/signals/events';
import { Store } from '@ngrx/store';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { mgcIconArrowDownRegular } from '@sb/mgc-icons/mgcIcon-arrow_down_regular.icon';
import { mgcIconArrowUpRegular } from '@sb/mgc-icons/mgcIcon-arrow_up_regular.icon';
import { mgcIconCornerDownLeftRegular } from '@sb/mgc-icons/mgcIcon-corner_down_left_regular.icon';
import { mgcIconDeleteBackRegular } from '@sb/mgc-icons/mgcIcon-delete_back_regular.icon';
import { canAccess, type AccessContext } from '@sb/shared/access';
import { FeatureService } from '@sb/shared/feature';
import { BadgeComponent } from '@sb/ui/badge';
import { SbDialogService } from '@sb/ui/dialog';
import { IconComponent, IconRegistry } from '@sb/ui/icon';

import 'ninja-keys';

import { NinjaKeys } from 'ninja-keys';
import { map } from 'rxjs/operators';

import { SidePanelService } from '../shared/app-side-panel/side-panel.service';
import { SBHeaderService } from '../shared/sb-header/sb-header.service';
import { MENU } from './command-palette.menu';
import { type MenuDeps, type MenuVisibilityContext, type NinjaAction } from './command-palette.model';
import { resolveMenu } from './command-palette.resolver';
import { commandPaletteEvents } from './events/command-palette.events';

// ninja-keys hardcodes `will-change: transform` on `.modal-content`, which
// promotes the panel to a permanent GPU compositor layer. Combined with the
// fractional `top: 20%` offset that lands on sub-pixel coordinates, rows
// render blurry at rest. Override it via a stylesheet adopted into the web
// component's shadow root.
const NINJA_KEYS_BLUR_FIX = new CSSStyleSheet();
NINJA_KEYS_BLUR_FIX.replaceSync(`.modal-content { will-change: auto; }`);

@Component({
  selector: 'command-palette',
  templateUrl: './command-palette.component.html',
  styleUrl: './command-palette.component.scss',
  imports: [TranslatePipe, BadgeComponent, IconComponent],
  encapsulation: ViewEncapsulation.None,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class CommandPaletteComponent {
  #translate = inject(TranslateService);
  #store = inject<Store<AppState>>(Store);
  #featureService = inject(FeatureService);
  #events = inject(Events);
  #userPreferences = inject(userPreferencesStore);
  #activeEmployees = this.#store.selectSignal(getActiveEmployees);
  #authUser = this.#store.selectSignal(getAuthenticatedUser);
  #permissionState = this.#store.selectSignal(getPermissionState);
  #subscription = this.#store.selectSignal(getAccountSubscription);
  // `?? false`: the preference is undefined until the store has loaded, and an unknown master
  // switch must not surface a Labs entry point in the meantime.
  #labsEnabled = computed(() => this.#userPreferences.labs_enabled() ?? false);
  #currentLang = toSignal(this.#translate.onLangChange.pipe(map((change) => change.lang)), {
    initialValue: this.#translate.getCurrentLang() ?? DefaultLocale,
  });

  #deps: MenuDeps = {
    router: inject(Router),
    dialog: inject(SbDialogService),
    sidePanel: inject(SidePanelService),
    header: inject(SBHeaderService),
    newHireDialog: inject(NewHireDialogService),
    templateDialog: inject(TemplateDialogService),
  };

  #items = computed(() => {
    this.#currentLang();

    const context: AccessContext = {
      permissionState: this.#permissionState(),
      subscription: this.#subscription(),
      isFeatureEnabled: (flag) => this.#featureService.isEnabledSignal(flag)(),
    };
    const visibility: MenuVisibilityContext = {
      permissionState: this.#permissionState(),
      isFeatureEnabled: (flag) => this.#featureService.isEnabledSignal(flag)(),
      labsEnabled: this.#labsEnabled(),
      hasMinijob: !!this.#authUser()?.minijob,
    };

    return resolveMenu({
      nodes: MENU,
      translate: (key) => this.#translate.instant(key),
      employees: this.#activeEmployees(),
      deps: this.#deps,
      canAccess: (constraint) => canAccess(constraint, context),
      visibility,
    });
  });

  ninjaKeys = viewChild<ElementRef<NinjaKeys>>('ninjaKeys');

  constructor() {
    inject(IconRegistry).registerIcons([
      mgcIconCornerDownLeftRegular,
      mgcIconDeleteBackRegular,
      mgcIconArrowUpRegular,
      mgcIconArrowDownRegular,
    ]);

    effect(() => {
      const ninja = this.ninjaKeys();
      if (ninja) {
        ninja.nativeElement.data = this.#items();
      }
    });

    effect(() => {
      const root = this.ninjaKeys()?.nativeElement.shadowRoot;
      if (root && !root.adoptedStyleSheets.includes(NINJA_KEYS_BLUR_FIX)) {
        root.adoptedStyleSheets = [...root.adoptedStyleSheets, NINJA_KEYS_BLUR_FIX];
      }
    });

    // Let non-keyboard entry points (the sidebar search button) open the palette.
    this.#events
      .on(commandPaletteEvents.openRequested)
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.open());
  }

  /** Opens the palette modal — the mouse-driven equivalent of the Cmd/Ctrl+K hotkey. */
  open(): void {
    this.ninjaKeys()?.nativeElement.open();
  }

  onActionSelected(event: Event): void {
    const { action } = (event as CustomEvent<{ action?: NinjaAction }>).detail;
    if (action?.handler) {
      // Close all open dialogs (if any) before executing the action handler, to ensure a clean state.
      this.#deps.dialog.closeAll();
    }
  }
}
